import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  UseFormSetValue,
} from 'react-hook-form';
import clsx from 'clsx';
import {
  Eventcalendar,
  MbscCellClickEvent,
  MbscEventcalendarView,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventDragEvent,
  MbscNewEventData,
  dayjsTimezone,
  MbscCalendarCellData,
  MbscCalendarDayData,
  MbscResource,
  MbscCalendarEventData,
} from '@mobiscroll/react';
import OfficeIcon from '@mui/icons-material/BusinessRounded';
import AvatarGroup from '@mui/material/AvatarGroup';
import map from 'lodash/map';
import keyBy from 'lodash/keyBy';
import groupBy from 'lodash/groupBy';
import head from 'lodash/head';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import {
  formatDate,
  formatDay,
  formatISODateTime,
  validateDateTimeRange,
} from 'utils/date';
import dayjs, {
  Dayjs,
  getDefaultTimeZoneId,
} from 'utils/dayjs';
import {
  getAddressString,
} from 'utils/string';
import {
  useMixpanelTrack,
  useNotify,
  useMedia,
  useSidePanel,
} from 'core/hooks';
import {
  isNilOrZero,
} from '@populateui/utils';
import {
  toEventPanelInfo,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/eventPanelUrl';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import {
  appointmentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  CalendarViewType,
  convertToCalendarViewEvents,
  DATE_UNIT_BY_VIEW_TYPE,
  eventTypeTitles,
  getAppointmentPlaceholderData,
} from 'pages/Dashboard/pages/Appointments/pages/List/utils/helper';
import {
  handleAppointmentActionDialog,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CreateAppointmentDialog';
import AppointmentAlertIndicator from 'pages/Dashboard/pages/Appointments/pages/List/components/AppointmentAlertIndicator';
import {
  CalendarViewSettings,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/SettingsButton';
import {
  useModifyAppointment,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  CalendarEvent,
  CalendarTimelineEvent,
  CalendarViewEvent,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import Name from 'pages/Dashboard/components/Name';
import ToolTip from 'core/components/Tooltip';
import Image from 'pages/Dashboard/components/Image';
import {
  FormattedNamesDTO,
  UserDTO,
} from 'dtos';
import EventCalendar from 'core/components/EventCalendar';

export type ResourceUser = UserDTO & {
  id: number;
};

type Props = {
  settings: CalendarViewSettings;
  appointments: CalendarEvent[];
  filteredAppointments: CalendarEvent[];
  doctors: ResourceUser[];
  setDate: (date: Dayjs) => void;
  currentDate: string;
  viewType: CalendarViewType;
  setViewType: (viewType: CalendarViewType) => void;
  forceRefresh?: boolean;
  setFilters: UseFormSetValue<any>;
  setBlockRefresh: Dispatch<SetStateAction<boolean>>;
};

const labelSteps: Record<string, number> = {
  large: 15,
  medium: 30,
  small: 60,
};

type FacilitiesTitleProps = {
  data: CalendarEvent[];
};

function FacilitiesTitle({ data }: FacilitiesTitleProps) {
  return (
    <div className="flex flex-col w-52 gap-2">
      {data.map(({ id, serviceFacility, color }) => (
        <div
          key={id}
          className="flex w-full gap-2 items-center"
        >
          <Image
            key={serviceFacility?.serviceFacilityId}
            size="md"
            variant="rounded"
            className="font-semibold"
            color={color}
          >
            <OfficeIcon className="!w-6 !h-6 !text-white" />
          </Image>
          <div className="flex flex-1 flex-col justify-center">
            <div className="font-semibold">{serviceFacility?.nickname ?? '--'}</div>
            <span>{getAddressString(serviceFacility?.address)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ScheduledEvent({
  original,
  ariaLabel,
  start,
  end,
}: MbscCalendarEventData) {
  const calendarEvent = original as CalendarViewEvent | undefined;
  const {
    background,
    title,
    editable,
    isTimeAvailability,
    patientAlerts,
  } = calendarEvent ?? {};
  return (
    <>
      {editable && (
        <>
          <div className="mbsc-schedule-event-resize mbsc-schedule-event-resize-start mbsc-ltr" />
          <div className="mbsc-schedule-event-resize mbsc-schedule-event-resize-end mbsc-ltr" />
        </>
      )}
      <div className="mbsc-schedule-event-bar mbsc-ios mbsc-ltr" />
      <ToolTip
        title={<FacilitiesTitle data={[original as CalendarEvent]} />}
        placement="left"
        hide={!isTimeAvailability}
      >
        <div className="content-container relative flex h-full w-full flex-1 overflow-hidden">
          <div
            className="mbsc-schedule-event-background mbsc-ios !w-full"
            style={{ background }}
          />
          <div
            aria-hidden="true"
            className="mbsc-schedule-event-inner mbsc-ios !w-full !text-black"
          >
            <div className="mbsc-schedule-event-title mbsc-ios">
              <span className="min-w-0 truncate">{title}</span>
            </div>
            <div className="mbsc-schedule-event-range mbsc-ios">
              {start}
              -
              {end}
            </div>
          </div>
          <div className="mbsc-hidden-content">{ariaLabel}</div>
          <AppointmentAlertIndicator alerts={patientAlerts} />
        </div>
      </ToolTip>
    </>
  );
}

function TimelineEvent({ original }: MbscCalendarEventData) {
  const {
    count,
    typeOfVisitDescription,
    isTimeAvailability,
    color,
  } = original as CalendarTimelineEvent;
  return (
    <>
      <div
        className="mbsc-schedule-event-background mbsc-timeline-event-background mbsc-ios bg-current"
      />
      <ToolTip
        hide={!isTimeAvailability}
        title={<FacilitiesTitle data={[original as CalendarEvent]} />}
      >
        <div
          aria-hidden="true"
          className="mbsc-schedule-event-inner !px-0.5 mbsc-ios"
        >
          <div className={clsx('mbsc-schedule-event-title mbsc-ios flex', {
            'flex-col': !isTimeAvailability,
            'items-center gap-1 py-1': isTimeAvailability,
          })}
          >
            {!isTimeAvailability && <span>{count}</span>}
            {isTimeAvailability && (
              <Image
                size="xs"
                className="font-semibold"
                color={color}
              >
                <OfficeIcon className="!w-4 !h-4 !text-white" />
              </Image>
            )}
            <span className="truncate">{typeOfVisitDescription}</span>
          </div>
        </div>
      </ToolTip>
    </>
  );
}

type DefaultFacilitiesProps = {
  availabilities: CalendarEvent[];
};

function DefaultFacilities({ availabilities }: DefaultFacilitiesProps) {
  return (
    <ToolTip title={<FacilitiesTitle data={availabilities} />}>
      <AvatarGroup
        className="!h-6"
        max={3}
        classes={{ avatar: '!h-6 !w-6 !text-xs' }}
      >
        {availabilities.map(({ id, color }) => (
          <Image
            key={id}
            size="xs"
            className="font-semibold"
            color={color}
          >
            <OfficeIcon className="!w-4 !h-4 !text-white" />
          </Image>
        ))}
      </AvatarGroup>
    </ToolTip>
  );
}

type DayHeaderProps = MbscCalendarDayData & DefaultFacilitiesProps;

function DayHeader({
  date, availabilities,
}: DayHeaderProps) {
  const [dayOfWeek, day] = formatDay(date).split(' ');
  return (
    <div className="flex flex-col items-center justify-center text-center text-sm cursor-pointer">
      <DefaultFacilities availabilities={availabilities} />
      <span className="font-semibold">{dayOfWeek}</span>
      <span>{day}</span>
    </div>
  );
}

type ResourceHeaderProps = MbscResource & {
  isDaily: boolean;
  availabilities: CalendarEvent[];
  formattedNames?: FormattedNamesDTO;
  userId?: number;
};

function ResourceHeader({
  id,
  userId,
  isDaily,
  availabilities,
  formattedNames,
}: ResourceHeaderProps) {
  const doctorUserId = userId ?? (id as number) ?? 0;
  const { requestMedia, getMediaPathById } = useMedia();

  useEffect(() => {
    if (doctorUserId > 0) {
      requestMedia([doctorUserId], MediaOriginatorType.User);
    }
  }, [doctorUserId, requestMedia]);

  const profilePictureUrl = getMediaPathById(doctorUserId, MediaOriginatorType.User);

  return (
    <div className="flex w-full flex-col items-center justify-end h-full cursor-pointer">
      {isDaily && <DefaultFacilities availabilities={availabilities} />}
      <Name
        image={{
          size: 'sm',
          variant: 'circular',
          src: profilePictureUrl ?? undefined,
        }}
        {...formattedNames}
        className="flex self-center justify-self-center"
        labelClassName="!text-xs !font-semibold !text-black"
      />
    </div>
  );
}

function CalendarView({
  appointments,
  filteredAppointments,
  doctors,
  setDate,
  currentDate,
  viewType,
  setViewType,
  setFilters,
  settings,
  setBlockRefresh,
}: Props) {
  const timezoneId = getDefaultTimeZoneId();
  const eventCalendarRef = useRef<Eventcalendar>(null);
  const notify = useNotify();
  const { openPanel } = useSidePanel();
  const { mutateAsync: updateAppointment } = useModifyAppointment();
  const mixpanelTrack = useMixpanelTrack();

  const doctorUserIds = useMemo(() => doctors.map((doctor) => doctor.userId ?? (doctor.id as number)), [doctors]);
  const { requestMedia } = useMedia();

  useEffect(() => {
    if (doctorUserIds.length > 0) {
      requestMedia(doctorUserIds, MediaOriginatorType.User);
    }
  }, [doctorUserIds, requestMedia]);

  const displayType = useMemo(() => DATE_UNIT_BY_VIEW_TYPE[viewType], [viewType]);
  const isWeeklyView = viewType === 'Weekly';
  const isMonthlyView = viewType === 'Monthly';
  const viewSize = settings?.viewSize ?? 'small';
  const timeLabelStep = labelSteps[viewSize];
  const calendarKey = `${viewType}-${viewSize}`;

  const filteredIdsSet = useMemo<Set<string>>(() => (
    new Set(map(filteredAppointments, 'id'))
  ), [filteredAppointments]);

  const availabilitiesByUserId = useMemo(() => (
    groupBy(appointments.filter(
      ({ isTimeAvailability, isDefault }) => isTimeAvailability && !isDefault,
    ), 'userId')), [appointments]);

  const defaultAvailabilities = useMemo(() => (
    groupBy(appointments.filter(
      ({ isTimeAvailability, isDefault }) => isTimeAvailability && isDefault,
    ), 'userId')), [appointments]);

  const calendarEvents = useMemo(
    () => (
      isMonthlyView
        ? filteredAppointments.filter(({ typeOfVisitDescription }) => typeOfVisitDescription !== '--')
        : convertToCalendarViewEvents(appointments, filteredIdsSet)
    ),
    [appointments, filteredAppointments, filteredIdsSet, isMonthlyView],
  );

  const todaySelected = useMemo(() => {
    const today = (displayType === 'week'
      ? dayjs().tz().day(0)
      : dayjs().tz());
    return formatDate(today) === currentDate;
  }, [currentDate, displayType]);

  // Ensure Mobiscroll scrolls to "now" in the display timezone
  // (selectedDate scrolls to the time part when present).
  const selectedDateTime = useMemo(() => (
    todaySelected ? formatISODateTime(dayjs().tz()) : currentDate
  ), [todaySelected, currentDate]);

  // Ensure the view scrolls to "now" when we're on today.
  useEffect(() => {
    if (isMonthlyView || !todaySelected) return;
    window.requestAnimationFrame(() => {
      eventCalendarRef.current?.navigate?.(selectedDateTime, false);
    });
  }, [isMonthlyView, todaySelected, selectedDateTime]);

  const view = useMemo<MbscEventcalendarView>(() => (
    isMonthlyView
      ? {
        timeline: {
          type: 'month',
          startDay: !settings.showWeekend ? 1 : 0,
          endDay: !settings.showWeekend ? 5 : 6,
        },
      }
      : {
        schedule: {
          type: displayType as 'day' | 'week',
          startDay: !settings.showWeekend && isWeeklyView ? 1 : 0,
          endDay: !settings.showWeekend && isWeeklyView ? 5 : 6,
          startTime: settings.showExtendedHours ? '00:00' : '07:00',
          endTime: settings.showExtendedHours ? '23:59' : '20:00',
          allDay: false,
          timeCellStep: 15,
          timeLabelStep,
          currentTimeIndicator: true,
        },
      }
  ), [displayType, isWeeklyView, isMonthlyView, settings, timeLabelStep]);

  const renderCell = useCallback(({ date }: MbscCalendarCellData) => {
    const cellTime = dayjs(date).tz(timezoneId);
    const minutesFromStartOfDay = cellTime.hour() * 60 + cellTime.minute();
    const shouldShowLine = !isMonthlyView
      && viewSize !== 'large'
      && minutesFromStartOfDay % timeLabelStep === 0;

    return shouldShowLine ? <div className="calendar-view-label-line" /> : null;
  }, [isMonthlyView, timeLabelStep, timezoneId, viewSize]);

  const getDoctorById = useCallback((userId: number) => (
    doctors.find((doctor) => doctor.userId === userId)
  ), [doctors]);

  const getEventById = useCallback((eventId: string) => (
    appointments.find(({ id }) => eventId === id)
  ), [appointments]);

  const getAvailabilityByEvent = useCallback((event: CalendarViewEvent) => {
    const doctorId = Number(event.resource);
    const eventStart = dayjs(event.start);

    return (availabilitiesByUserId[doctorId] ?? []).find((appointment) => {
      const {
        isTimeAvailability, userId, start, end,
      } = appointment;
      const belongsToDoctor = userId === doctorId;
      const isInRange = eventStart.isBetween(start, end, 'minutes', '[)');

      return isTimeAvailability && belongsToDoctor && isInRange;
    });
  }, [availabilitiesByUserId]);

  const removePlaceholder = () => {
    const instance = eventCalendarRef.current;
    const placeholders = instance?.getEvents().filter((event) => event.placeholder);
    instance?.removeEvent(placeholders ?? []);
  };

  const getOtherEvents = (event: CalendarViewEvent, calendarViewEvents: CalendarViewEvent[]) => {
    const visibleEvents = keyBy(calendarViewEvents, 'id');

    return (
      appointments
        .map(({
          start,
          end,
          userId,
          ...appointment
        }) => {
          const calendarViewEvent = visibleEvents[appointment.id];
          return {
            ...appointment,
            ...(!isNil(calendarViewEvent)
              ? {
                start: dayjs(calendarViewEvent.start),
                end: dayjs(calendarViewEvent.end),
                userId: Number(calendarViewEvent.resource),
              }
              : { start, end, userId }
            ),
          };
        })
        .filter((otherEvent) => (
          event.id !== otherEvent.id
          && (
            (!isNil(event?.patientId) && event?.patientId === otherEvent?.patient?.patientId)
            || (
              (event.isTimeBlock || otherEvent.isTimeBlock)
              && Number(event.resource) === otherEvent.userId
            )
          )
          && !event.isTimeBlock
          && !otherEvent.isTimeBlock
          && dayjs(event.start).isSame(otherEvent.start, 'day')
        )) ?? []
    );
  };

  const getDefaultEvent = (args: MbscNewEventData) => ({
    ...args,
    ...getAppointmentPlaceholderData({
      defaultStartDateTime: dayjs(args.start),
    }),
  });

  const onCellClick = (args?: MbscCellClickEvent) => {
    const { date, resource } = args ?? {};
    if (isMonthlyView && !isNil(date) && !isNil(resource)) {
      setDate(dayjs(date));
      setFilters('userId', [Number(resource)]);
      setViewType('Daily');
    }
  };

  const onEventClick = (args?: MbscEventClickEvent) => {
    const { resource, date, event } = args ?? {};
    if (isMonthlyView) {
      if (!isNil(date) && !isNil(resource)) {
        onCellClick({ date, resource } as MbscCellClickEvent);
      }
    } else {
      const calendarEvent = getEventById(event?.id as string);
      if (!isNil(calendarEvent) && !event?.allDay) {
        if (!isNilOrZero(calendarEvent.objectId)) {
          openPanel('event', toEventPanelInfo({
            id: calendarEvent.objectId,
            isTimeBlock: calendarEvent.isTimeBlock,
            isAvailability: calendarEvent.isTimeAvailability,
            eventDate: dayjs((event?.start ?? '') as Date),
          }));
        }
      }
    }
  };

  const onEventCreate = ({ event: _event, action }: MbscEventCreateEvent) => {
    if (!isMonthlyView) {
      const event = _event as CalendarViewEvent;
      const eventAvailability = getAvailabilityByEvent(event);

      const isValid = validateDateTimeRange([dayjs(event.start), dayjs(event.end)]);

      if (isValid) {
        appointmentsEventChannel.emit('onEventCreate', {
          defaultStartDateTime: dayjs(event.start).tz(),
          defaultDurationInMinutes: action === 'drag'
            ? dayjs(event.end).diff(dayjs(event.start), 'minute')
            : undefined,
          defaultDoctor: getDoctorById(Number(event.resource)),
          defaultServiceFacility: eventAvailability?.serviceFacility,
        });
        mixpanelTrack('createAppointment', { source: 'calendar' });
      } else {
        notify({
          variant: 'error',
          message: 'Appointment cannot be longer than 24 hours.',
        });
      }

      return isValid;
    }

    return false;
  };

  const isUpdateAllowed = (event: CalendarViewEvent, calendar: Eventcalendar) => {
    if (!event.isTimeAvailability) {
      // checking if appointment duration is less than 24 hours
      if (!validateDateTimeRange([dayjs(event.start), dayjs(event.end)])) {
        notify({
          variant: 'error',
          message: 'Appointment cannot be longer than 24 hours.',
        });
        return false;
      }

      // checking if the same patient for same user
      // doesn't have another appointment same time (or between)
      const hasConflictedEvent = getOtherEvents(event, calendar?.getEvents() as CalendarViewEvent[])
        .some(({ start, end, userId }) => (
          (dayjs(event.start).isBetween(start, end, 'minutes', '[)')
          || dayjs(event.end).isBetween(start, end, 'minutes', '(]')
          || start.isBetween(event.start, event.end, 'minutes')
          || end.isBetween(event.start, event.end, 'minutes'))
          && event.resource === userId
        ));

      if (hasConflictedEvent) {
        notify({
          variant: 'error',
          message: 'An appointment for the provider already exists on the time slot.',
        });
        return false;
      }
    }

    return true;
  };
  const revertEventChange = (event: CalendarEvent, calendar: Eventcalendar) => {
    // reverting event back
    const allEvents = calendar?.getEvents() ?? [];

    const originalEvent = head(
      convertToCalendarViewEvents([event], filteredIdsSet),
    );

    const eventIndex = allEvents.findIndex((event) => event.id === originalEvent?.id);

    calendar?.setEvents([
      ...allEvents.slice(0, eventIndex),
      originalEvent ?? {},
      ...allEvents.slice(eventIndex + 1),
    ]);
  };

  const onEventUpdate = async ({ event: _event, inst }: MbscEventDragEvent) => {
    const event = _event as CalendarViewEvent;
    const calendar = inst as Eventcalendar;
    const calendarEvent = getEventById(event.id as string);
    const hasEvent = !isNil(calendarEvent);
    const isAllowed = isUpdateAllowed(event, calendar);

    if (!isMonthlyView && hasEvent && isAllowed) {
      try {
        const start = dayjs(event.start).tz();
        const end = dayjs(event.end).tz();
        const durationInMinutes = end.diff(start, 'minutes');

        setBlockRefresh(true);
        const response = await updateAppointment({
          ...calendarEvent,
          userId: Number(event.resource),
          start,
          end,
          durationInMinutes,
        });

        const eventName = eventTypeTitles[calendarEvent?.typeName ?? 'appointment'];
        handleAppointmentActionDialog(
          response,
          notify,
          `${eventName} updated.`,
        );

        appointmentsEventChannel.emit('onEventUpdated', calendarEvent.objectId, false);
      } catch (e: unknown) {
        revertEventChange(calendarEvent, calendar);
      }
    } else if (!isAllowed) {
      revertEventChange(calendarEvent!, calendar);
    }
  };

  useEffect(() => {
    const unsubscribeFromCreated = appointmentsEventChannel.on(
      'onEventCreated',
      (created) => {
        if (!created) {
          removePlaceholder();
        }
      },
    );

    return () => {
      unsubscribeFromCreated();
    };
  }, []);

  return (
    <EventCalendar
      key={calendarKey}
      classes={{
        page: clsx(
          `calendar-view-${viewSize}`,
          `calendar-view-label-step-${timeLabelStep}`,
          `calendar-view-${viewType.toLowerCase()}`,
        ),
      }}
      ref={eventCalendarRef as RefObject<Eventcalendar>}
      view={view}
      displayTimezone={timezoneId}
      dataTimezone={timezoneId}
      timezonePlugin={dayjsTimezone}
      resources={doctors}
      data={calendarEvents}
      renderCell={renderCell}
      selectedDate={selectedDateTime}
      clickToCreate={isMonthlyView ? false : 'single'}
      dragToMove={!isMonthlyView}
      dragToCreate={!isMonthlyView}
      dragToResize={!isMonthlyView}
      extendDefaultEvent={getDefaultEvent}
      showEventTooltip={false}
      renderDay={(dayEvent: MbscCalendarDayData) => (
        <DayHeader
          {...dayEvent}
          availabilities={defaultAvailabilities[dayEvent?.resource ?? 0] ?? []}
        />
      )}
      renderResource={(resource) => (
        <ResourceHeader
          {...resource}
          isDaily={!isMonthlyView && !isWeeklyView}
          availabilities={defaultAvailabilities[resource.id ?? 0] ?? []}
        />
      )}
      renderScheduleEvent={isMonthlyView
        ? ({ key, ...event }) => <TimelineEvent {...event} />
        : ScheduledEvent}
      renderHeader={() => null}
      onEventClick={onEventClick}
      onEventCreate={onEventCreate}
      onEventUpdate={onEventUpdate}
      onCellClick={onCellClick}
    />
  );
}

const getSortedIdsString = (appointments: CalendarEvent[]) => (
  map(appointments, 'encounterId').sort((a: number = 0, b: number = 0) => a - b).join('')
);

export default memo(CalendarView, ({
  appointments: currentAppointments,
  filteredAppointments: filteredCurrent,
  settings: currentSettings,
  doctors: currentDoctors,
  currentDate,
  viewType,
}, {
  appointments: incomingAppointments,
  filteredAppointments: filteredIncoming,
  forceRefresh,
  settings: incomingSettings,
  doctors: incomingDoctors,
  currentDate: incomingDate,
  viewType: incomingViewType,
}) => {
  if (
    forceRefresh
    || (currentAppointments.length !== incomingAppointments.length)
    || (filteredCurrent.length !== filteredIncoming.length)
    || !isEqual(currentSettings, incomingSettings)
    || !isEqual(currentDoctors, incomingDoctors)
    || currentDate !== incomingDate
    || viewType !== incomingViewType
  ) return false;

  return getSortedIdsString(currentAppointments) === getSortedIdsString(incomingAppointments);
});
